import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  Observable,
  pairwise,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { SessionQuery } from '../../../core/state';
import { ManageBorrowersModalComponent } from '../../../cmg-1003/components/borrower-information/manage-borrowers-modal/manage-borrowers-modal.component';
import { Cmg1003ApplicationInfoQuery, Cmg1003StoreQueryService } from '../../../loan-state';
import { DialogService } from 'primeng/dynamicdialog';

export interface NavBarItemBase {
  sectionType: any;
  text: string;
  position: number;
}

/**
 * This base component was created to easily share functionality
 * between the shared navbar and the 1003-navbar
 */
@Component({
  template: '',
  standalone: false,
})
export class NavbarBaseComponent implements AfterViewInit, OnDestroy, OnInit {
  protected readonly _applicationsQuery = inject(Cmg1003ApplicationInfoQuery);
  protected readonly _cdRef = inject(ChangeDetectorRef);
  protected readonly _cmgStoreQuery = inject(Cmg1003StoreQueryService);
  protected readonly _dialogService = inject(DialogService);
  protected readonly _formBuilder = inject(UntypedFormBuilder);
  protected readonly _sessionQuery = inject(SessionQuery);

  // Always add this class to the component
  @HostBinding('class.urla-navbar') navbarClass = true;

  readonly navBarSections = viewChild('navBarSections', { read: ElementRef });

  readonly hideNewApplication = input(false);
  readonly hideNavbarMenu = input(false);
  @Input() navBarItems: NavBarItemBase[] = [];

  readonly navbarItemClicked = output<any>();
  readonly newApplicationAdded = output<void>();
  readonly changeExistingApplication = output<void>();

  protected _subscriptions = new Subscription();
  protected _appSelectorChangeSubscription: Subscription;
  protected readonly _destroyed$ = new Subject<boolean>();

  activeNavbarItem: number = 0;
  isStartOfScroll$: Observable<boolean>;
  isEndOfScroll$: Observable<boolean>;
  scrollPosition = this.activeNavbarItem.valueOf();
  appForm = this._formBuilder.group({
    appSelector: new UntypedFormControl(),
    appSelectorDisplay: new UntypedFormControl(),
  });

  ngOnInit() {
    this._subscriptions.add(
      this._applicationsQuery
        .selectActiveId()
        .pipe(
          filter((i) => i !== undefined),
          distinctUntilChanged(),
        )
        .subscribe((appId) => {
          this.appForm.get('appSelector').setValue(appId, { emitEvent: false, onlySelf: true });
          this.appForm.get('appSelectorDisplay').setValue(appId);
          if (!this._appSelectorChangeSubscription) {
            this._appSelectorChangeSubscription = this.appForm
              .get('appSelector')
              .valueChanges.pipe(
                startWith(this.appForm.get('appSelector').value),
                pairwise(),
                map(([oldApplicationId, newApplicationId]) => {
                  return {
                    oldApplicationId: oldApplicationId,
                    newApplicationId: newApplicationId,
                  };
                }),
              )
              .subscribe(this._onSelectedApplicationChanged);

            this._subscriptions.add(this._appSelectorChangeSubscription);
          }
        }),
    );

    this._subscriptions.add(
      this._sessionQuery
        .select((t) => t.scrollAdjustNavItem)
        .pipe(distinctUntilChanged())
        .subscribe(this._updateActiveTab),
    );
  }

  ngAfterViewInit() {
    this._setupSectionButtons();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  private _setupSectionButtons() {
    if (this.hideNavbarMenu()) {
      return;
    }

    this._subscriptions.add(
      /**
       *  No end event for scrolling
       *  When scrolling has stopped
       *  scroll nav bar item into view of the current section
       */
      fromEvent(window, 'scroll')
        .pipe(
          distinctUntilChanged(),
          debounceTime(100), // wait 100 ms if no emit then scroll stopped
          switchMap(() => this._sessionQuery.select((t) => t.scrollAdjustNavItem)),
        )
        .subscribe(() => this._updateActiveTab),
    );
    const windowResize$ = fromEvent(window, 'resize').pipe(debounceTime(100), startWith(null));
    const navBarScrollEvent$ = fromEvent(this.navBarSections().nativeElement, 'scroll').pipe(
      startWith(0),
      distinctUntilChanged(),
      debounceTime(100), // wait 100 ms if no emit then scroll stopped
    );
    const startEndMarginOfError = 4; // Number of pixels within the start/end that will start count as the start/end
    this.isStartOfScroll$ = combineLatest([navBarScrollEvent$, windowResize$]).pipe(
      map(() => {
        const currentScrollLeft = this.navBarSections().nativeElement.scrollLeft;
        return currentScrollLeft <= startEndMarginOfError;
      }),
      distinctUntilChanged(),
    );
    this.isEndOfScroll$ = combineLatest([navBarScrollEvent$, windowResize$]).pipe(
      map(() => {
        const container = this.navBarSections().nativeElement;
        return container.scrollLeft + container.clientWidth >= container.scrollWidth - startEndMarginOfError;
      }),
      distinctUntilChanged(),
    );
  }

  protected _scrollNavBarToCurrentSection() {
    const navBarSections = this.navBarSections();
    if (!navBarSections) {
      return;
    }
    const offsetLeft = this._getSectionElement()?.offsetLeft;
    navBarSections.nativeElement.scrollLeft = offsetLeft - navBarSections.nativeElement.offsetLeft;
  }

  protected _getSectionElement() {
    return document.getElementById('section-' + this.scrollPosition.toString());
  }

  protected _showManageBorrowersModel() {
    this._cmgStoreQuery
      .selectApplicationsAndBorrowers()
      .pipe(take(1))
      .subscribe((t) => {
        this._dialogService.open(ManageBorrowersModalComponent, {
          closeOnEscape: false,
          dismissableMask: false,
          position: 'center',
          showHeader: false,
          keepInViewport: true,
          styleClass: 'manage-borrowers-modal',
          data: t,
        });
      });
  }

  /**
   * This should be overridden in any class that extends NavBarBaseComponent
   * @param changeObj
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected _onSelectedApplicationChanged = (changeObj: { oldApplicationId: number; newApplicationId: number }) => {};

  protected readonly _updateActiveTab = (scrolledToSection: any) => {
    //  TODO look into putting this into stream where _updateActiveTab gets called in subscribe
    //  This gets set to true in _switchApplication and back to false in cmg-1003.components.ts
    const pause = this._sessionQuery.getValue().pauseUpdateActiveTab;
    if (!pause) {
      this.activeNavbarItem = scrolledToSection;
      this.scrollPosition = scrolledToSection;
      this._scrollNavBarToCurrentSection();
      this._cdRef.detectChanges();
    }
  };

  onNavBarItemClick(selectedNavbarItem: NavBarItemBase) {
    this.activeNavbarItem = selectedNavbarItem.sectionType;
    this.scrollPosition = selectedNavbarItem.position;
    this.navbarItemClicked.emit(selectedNavbarItem.sectionType);
  }

  onMobileNavSelect(selectedNavbarSection: any, navBarItems: NavBarItemBase[]) {
    const selectedNavbarItem = navBarItems.find((item) => item.sectionType === selectedNavbarSection);
    if (selectedNavbarItem) {
      this.onNavBarItemClick(selectedNavbarItem);
    }
  }

  /**
   *
   * applicationId Positive: On Changing Selection check if application changed, if not then const them switch
   * applicationId Negative: On Changing Selection check if applicationId changed, if not then const them switch
   * @param changeObj
   */
  onApplicationSelectorDisplayChange(event: any): void {
    if (event?.value) {
      this.appForm.get('appSelector').setValue(event.value, { emitEvent: true, onlySelf: true });
    }
  }

  scrollNav(direction: 'left' | 'right') {
    const overlapPixels = 20; // Display this many of the same pixels from the previous "page"
    const container = this.navBarSections().nativeElement;

    switch (direction) {
      case 'left':
        container.scrollLeft = container.scrollLeft - container.clientWidth - overlapPixels;
        break;

      case 'right':
        container.scrollLeft = container.scrollLeft + container.clientWidth - overlapPixels;
    }
  }
}
